/**
 * A founder's review of an investor.
 *
 * Only reachable when `investor_startup_have_engaged()` says this startup has
 * a real relationship with the investor — the same predicate the INSERT policy
 * uses, so the action and the write agree. Hiding the button is a courtesy;
 * RLS is what actually prevents an ineligible review.
 *
 * ANONYMITY IS THE POINT OF THE TOGGLE. A founder rating an investor two stars
 * under their own name is taking a career risk, and a review system that
 * ignores that collects only praise. The flag is honoured server-side by
 * `get_investor_reviews()`, which returns NULL for the author and company —
 * the reader never receives the identity, so it cannot leak through the client.
 */

import { useState } from 'react';
import { View, Text, TextInput, ActivityIndicator } from 'react-native';
import { Star, Send, EyeOff, Eye } from 'lucide-react-native';

import { AppModal, ModalAction } from '@/components/modal';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAlert } from '@/components/AlertDialog';
import { handleError } from '@/lib/errors';
import { useSubmitInvestorReview } from '@/lib/investor/write-review';

interface WriteReviewModalProps {
  visible: boolean;
  onClose: () => void;
  investorUserId: string;
  investorName: string;
  startupId: string;
  authorUserId: string;
}

const RATING_HINTS: Record<number, string> = {
  1: 'Difficult to work with',
  2: 'Below expectations',
  3: 'Fine, unremarkable',
  4: 'Genuinely helpful',
  5: 'Would take their money again',
};

export function WriteReviewModal({
  visible,
  onClose,
  investorUserId,
  investorName,
  startupId,
  authorUserId,
}: WriteReviewModalProps) {
  const colors = useThemeColors();
  const { alert, success } = useAlert();
  const submit = useSubmitInvestorReview();

  const [rating, setRating] = useState(0);
  const [body, setBody] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);

  const canSubmit = rating > 0 && body.trim().length > 0 && !submit.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await submit.mutateAsync({
        investorUserId,
        startupId,
        authorUserId,
        rating,
        body,
        isAnonymous,
      });
      success('Review published', `Your review of ${investorName} is live.`);
      setRating(0);
      setBody('');
      setIsAnonymous(false);
      onClose();
    } catch (err) {
      const { message, isCancelled } = handleError(err, {
        context: 'WriteReviewModal.submit',
      });
      if (!isCancelled) alert("Couldn't publish your review", message);
    }
  };

  const footer = (
    <AppModal.Actions>
      <ModalAction
        label="Cancel"
        variant="neutral"
        onPress={onClose}
        accessibilityLabel="Cancel review"
      />
      <ModalAction
        label={submit.isPending ? 'Publishing…' : 'Publish review'}
        variant="primary"
        disabled={!canSubmit}
        icon={
          submit.isPending ? (
            <ActivityIndicator size="small" color="white" />
          ) : (
            <Send size={18} color="white" />
          )
        }
        onPress={handleSubmit}
        accessibilityLabel="Publish review"
      />
    </AppModal.Actions>
  );

  return (
    <AppModal
      visible={visible}
      onClose={onClose}
      title={`Review ${investorName}`}
      maxWidth={520}
      footer={footer}
    >
      <Text style={{ color: colors.text.secondary }} className="text-sm leading-5 mb-5">
        Other founders will see this when deciding whether to take a meeting.
        Be specific about what actually happened.
      </Text>

      {/* Rating */}
      <Text
        style={{ color: colors.text.tertiary }}
        className="text-xs font-semibold uppercase mb-2"
        numberOfLines={1}
      >
        Your rating
      </Text>
      <View className="flex-row items-center gap-2 mb-1">
        {[1, 2, 3, 4, 5].map((value) => (
          <AnimatedPressable
            key={`star-${value}`}
            onPress={() => setRating(value)}
            accessibilityLabel={`${value} star${value === 1 ? '' : 's'}`}
            className="p-1"
          >
            <Star
              size={30}
              color={value <= rating ? colors.amber.main : colors.border.dark}
              fill={value <= rating ? colors.amber.main : 'transparent'}
            />
          </AnimatedPressable>
        ))}
      </View>
      {/* Reserve the line whether or not a rating is picked, so choosing one
          does not shift everything below it. */}
      <Text
        style={{ color: rating > 0 ? colors.text.secondary : colors.text.tertiary }}
        className="text-sm mb-5"
        numberOfLines={1}
      >
        {rating > 0 ? RATING_HINTS[rating] : 'Tap to rate'}
      </Text>

      {/* Body */}
      <Text
        style={{ color: colors.text.tertiary }}
        className="text-xs font-semibold uppercase mb-2"
        numberOfLines={1}
      >
        What was it like working with them?
      </Text>
      <TextInput
        value={body}
        onChangeText={setBody}
        placeholder="How fast did they reply? Were they straight with you about a no? Did they help after the cheque?"
        placeholderTextColor={colors.text.tertiary}
        multiline
        maxLength={4000}
        style={{
          color: colors.text.primary,
          backgroundColor: colors.surface.secondary,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: colors.border.light,
          padding: 14,
          minHeight: 130,
          textAlignVertical: 'top',
          fontSize: 15,
        }}
      />

      {/* Anonymity */}
      <AnimatedPressable
        onPress={() => setIsAnonymous((v) => !v)}
        className="flex-row items-center gap-3 mt-5 p-4 rounded-xl"
        style={{
          backgroundColor: isAnonymous ? colors.primary[50] : colors.surface.secondary,
          borderWidth: 1,
          borderColor: isAnonymous ? colors.primary[500] : colors.border.light,
        }}
        accessibilityLabel="Toggle anonymous review"
        accessibilityRole="checkbox"
        accessibilityState={{ checked: isAnonymous }}
      >
        <View
          className="w-9 h-9 rounded-full items-center justify-center"
          style={{ backgroundColor: isAnonymous ? colors.primary[100] : colors.light[300] }}
        >
          {isAnonymous ? (
            <EyeOff size={17} color={colors.primary[600]} />
          ) : (
            <Eye size={17} color={colors.text.tertiary} />
          )}
        </View>
        <View className="flex-1">
          <Text
            style={{ color: isAnonymous ? colors.primary[700] : colors.text.primary }}
            className="font-medium"
            numberOfLines={1}
          >
            Post anonymously
          </Text>
          <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5" numberOfLines={2}>
            {isAnonymous
              ? 'Your name and company stay hidden — the investor never receives them.'
              : `Shown as you and your company.`}
          </Text>
        </View>
      </AnimatedPressable>
    </AppModal>
  );
}
